import { useState } from "react";
import { COMPLETING_FOR } from "../../lib/options";
import { buildPayload, useSubmit, validateContact, validateLocation } from "../../lib/forms";
import { Wizard } from "./Wizard";
import { ChoiceCards, ConsentBlock, ContactFields, Field, Group, Honeypot, LocationFields, MultiChips, NativeSelect, SuccessPanel, TextArea, TextField } from "./FormBits";
import { Notice } from "../site/Blocks";

const AGES = ["Under 65", "65–74", "75–84", "85–94", "95 or older"];
const LIVING = ["Lives alone at home", "Lives with family", "Currently in hospital", "In a retirement residence", "In long-term care", "Other"];
const CARE_NEEDS = ["Bathing and showering", "Dressing and grooming", "Toileting and continence care", "Meals and help with eating",
  "Medication reminders", "Medication administration", "Mobility and transfers", "Fall prevention and supervision",
  "Memory care or dementia support", "Overnight supervision", "Housekeeping and laundry", "Social and recreational activities", "Other"];
const CONDITIONS = ["Diabetes", "Heart condition", "History of stroke", "Parkinson's disease", "Breathing condition (e.g. COPD)",
  "Arthritis or chronic pain", "Vision or hearing loss", "Mental health condition", "Other"];
const MOBILITY = ["Walks independently", "Uses a cane", "Uses a walker", "Uses a wheelchair", "Needs help with transfers", "Mostly in bed"];
const MEMORY = ["No memory concerns", "Mild forgetfulness", "Diagnosed dementia", "Dementia with risk of wandering", "Not sure"];
const PALLIATIVE = ["No", "Yes, receiving palliative care", "Yes, receiving hospice or end-of-life care", "Not sure", "Prefer to discuss in person"];
const MOVE = ["Within 3 months", "In 3–6 months", "In 6–12 months", "Just planning ahead"];
const GOV = "Government or agency funding";
const COVERAGE = ["Private pay", "Family support", "Pension and savings", "Private or long-term care insurance", GOV, "Not sure yet", "Other"];

const INITIAL = {
  completing_for: "", relationship: "", organization: "", resident_first_name: "", person_aware: "", age_range: "",
  region: "", city: "", city_custom: "", living_situation: "", living_other: "", care_needs: [], care_needs_other: "", mobility: "", memory: "",
  conditions: [], conditions_other: "", palliative: "",
  nursing_needs: "", nursing_details: "", dietary_needs: "", language_preference: "", move_timeframe: "", coverage: "",
  coverage_other: "", funding_details: "", additional_notes: "", name: "", email: "", phone: "", preferred_contact: "",
  privacy_consent: false, marketing_consent: false, website: "",
};

const clean = (f) => {
  const c = { ...f };
  if (f.completing_for === "self") Object.assign(c, { relationship: "", organization: "", resident_first_name: "", person_aware: "" });
  if (f.completing_for === "professional") c.relationship = "";
  if (["family", "friend"].includes(f.completing_for)) c.organization = "";
  if (f.nursing_needs !== "Yes") c.nursing_details = "";
  if (f.coverage !== "Other") c.coverage_other = "";
  if (f.coverage !== GOV) c.funding_details = "";
  if (f.living_situation !== "Other") c.living_other = "";
  if (!f.care_needs.includes("Other")) c.care_needs_other = "";
  if (!f.conditions.includes("Other")) c.conditions_other = "";
  return c;
};

const About = ({ form, set }) => {
  const forOther = form.completing_for && form.completing_for !== "self";
  return (
    <>
      <Group legend="Who is this inquiry for?" required>
        <ChoiceCards options={COMPLETING_FOR} value={form.completing_for} onChange={(v) => set("completing_for", v)} testId="assisted-completing-for" />
      </Group>
      {forOther && (
        <div className="grid sm:grid-cols-2 gap-5 animate-step">
          {form.completing_for === "professional" ? (
            <TextField id="assisted-org" label="Organization (optional)" value={form.organization} onChange={(v) => set("organization", v)} testId="assisted-input-organization" />
          ) : (
            <TextField id="assisted-rel" label="Your relationship to the senior" value={form.relationship} onChange={(v) => set("relationship", v)} placeholder="e.g. daughter, son" testId="assisted-input-relationship" />
          )}
          <TextField id="assisted-resident" label="Their first name (optional)" value={form.resident_first_name} onChange={(v) => set("resident_first_name", v)} testId="assisted-input-resident-name" />
          <Field label="Is the senior aware of this inquiry?" htmlFor="assisted-aware">
            <NativeSelect id="assisted-aware" value={form.person_aware} onChange={(v) => set("person_aware", v)} testId="assisted-input-person-aware"
              options={["Yes, they are aware and agree", "Not yet", "They need support with decision-making", "Prefer to discuss"]} />
          </Field>
        </div>
      )}
      <Group legend="Age range of the senior needing care" required>
        <ChoiceCards options={AGES} value={form.age_range} onChange={(v) => set("age_range", v)} cols="grid-cols-2 sm:grid-cols-5" testId="assisted-age" />
      </Group>
    </>
  );
};

export const AssistedLivingQuestionnaire = () => {
  const [form, setForm] = useState(INITIAL);
  const { submit, submitting, error, reference, reset } = useSubmit();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (reference) {
    return (
      <SuccessPanel reference={reference} title="Thank you. You have joined our assisted living interest list." testId="assisted-success"
        onReset={() => { setForm(INITIAL); reset(); }}>
        <p>We will keep your details on file and contact you as opening and admission details for our senior care home are confirmed.</p>
        <p>Joining the interest list does not reserve a room or guarantee admission.</p>
      </SuccessPanel>
    );
  }

  const steps = [
    {
      title: "About the senior", short: "About",
      validate: () => (!form.completing_for ? "Please tell us who this inquiry is for." : !form.age_range ? "Please select an age range." : null),
      content: <About form={form} set={set} />,
    },
    {
      title: "Current living situation", short: "Living situation",
      validate: () => validateLocation(form) || (!form.living_situation ? "Please tell us where the senior is living now."
        : form.living_situation === "Other" && !form.living_other.trim() ? "Please describe the current living situation." : null),
      content: (
        <>
          <LocationFields form={form} set={set} prefix="assisted" legend="Where does the senior currently live?" />
          <Group legend="Current living situation" required>
            <ChoiceCards options={LIVING} value={form.living_situation} onChange={(v) => set("living_situation", v)} cols="sm:grid-cols-2 lg:grid-cols-3" testId="assisted-living-situation" />
          </Group>
          {form.living_situation === "Other" && (
            <TextField id="assisted-living-other" label="Please describe" value={form.living_other} onChange={(v) => set("living_other", v)} required maxLength={400} testId="assisted-input-living-other" />
          )}
        </>
      ),
    },
    {
      title: "Daily care needs", short: "Care needs", description: "Select all the help the senior needs day to day.",
      validate: () => (form.care_needs.length === 0 ? "Please select at least one type of care needed."
        : form.care_needs.includes("Other") && !form.care_needs_other.trim() ? "Please describe the other care needs."
        : !form.mobility ? "Please tell us about mobility." : !form.memory ? "Please tell us about memory and cognitive support." : null),
      content: (
        <>
          <Group legend="What type of support is needed?" required>
            <MultiChips options={CARE_NEEDS} value={form.care_needs} onChange={(v) => set("care_needs", v)} testId="assisted-care" />
          </Group>
          {form.care_needs.includes("Other") && (
            <TextField id="assisted-care-other" label="Other care needs" value={form.care_needs_other} onChange={(v) => set("care_needs_other", v)} required maxLength={400} testId="assisted-input-care-other" />
          )}
          <Group legend="Mobility limitations" required>
            <ChoiceCards options={MOBILITY} value={form.mobility} onChange={(v) => set("mobility", v)} cols="sm:grid-cols-2 lg:grid-cols-3" testId="assisted-mobility" />
          </Group>
          <Group legend="Memory and cognitive support" required>
            <ChoiceCards options={MEMORY} value={form.memory} onChange={(v) => set("memory", v)} cols="sm:grid-cols-2 lg:grid-cols-3" testId="assisted-memory" />
          </Group>
        </>
      ),
    },
    {
      title: "Health considerations", short: "Health", description: "General information only. Please do not share detailed medical records.",
      validate: () => (form.conditions.includes("Other") && !form.conditions_other.trim() ? "Please describe the other health condition."
        : !form.palliative ? "Please answer the palliative care question." : !form.nursing_needs ? "Please answer the nursing needs question." : null),
      content: (
        <>
          <Group legend="General health conditions (optional)" hint="Select any that apply. No medical records are needed.">
            <MultiChips options={CONDITIONS} value={form.conditions} onChange={(v) => set("conditions", v)} testId="assisted-condition" />
          </Group>
          {form.conditions.includes("Other") && (
            <TextField id="assisted-conditions-other" label="Other health condition" value={form.conditions_other} onChange={(v) => set("conditions_other", v)} required maxLength={400} testId="assisted-input-conditions-other" />
          )}
          <Group legend="Is the senior receiving palliative, hospice or end-of-life care?" required>
            <ChoiceCards options={PALLIATIVE} value={form.palliative} onChange={(v) => set("palliative", v)} testId="assisted-palliative" />
          </Group>
          <Group legend="Does the senior have ongoing nursing needs (for example wound care, oxygen, catheter care or diabetes management)?" required>
            <ChoiceCards options={["No", "Yes", "Not sure"]} value={form.nursing_needs} onChange={(v) => set("nursing_needs", v)} cols="sm:grid-cols-3" testId="assisted-nursing" />
          </Group>
          {form.nursing_needs === "Yes" && (
            <TextField id="assisted-nursing-details" label="Briefly, what nursing needs? (optional)" value={form.nursing_details} onChange={(v) => set("nursing_details", v)} maxLength={400} testId="assisted-input-nursing-details" />
          )}
          <div className="grid sm:grid-cols-2 gap-5">
            <TextField id="assisted-diet" label="Dietary needs (optional)" value={form.dietary_needs} onChange={(v) => set("dietary_needs", v)} maxLength={400} placeholder="e.g. diabetic, soft foods, halal" testId="assisted-input-dietary" />
            <TextField id="assisted-language" label="Preferred language (optional)" value={form.language_preference} onChange={(v) => set("language_preference", v)} testId="assisted-input-language" />
          </div>
        </>
      ),
    },
    {
      title: "Timing and coverage", short: "Timing",
      validate: () => (!form.move_timeframe ? "Please select when the senior may need to move." : !form.coverage ? "Please select the anticipated funding or coverage."
        : form.coverage === "Other" && !form.coverage_other.trim() ? "Please describe the other funding or coverage."
        : form.coverage === GOV && !form.funding_details.trim() ? "Please tell us which program or agency provides the funding." : null),
      content: (
        <>
          <Group legend="When might the senior need to move?" required>
            <ChoiceCards options={MOVE} value={form.move_timeframe} onChange={(v) => set("move_timeframe", v)} cols="sm:grid-cols-2" testId="assisted-move" />
          </Group>
          <Group legend="Anticipated funding or coverage" required hint="How do you expect care to be funded?">
            <ChoiceCards options={COVERAGE} value={form.coverage} onChange={(v) => set("coverage", v)} testId="assisted-coverage" />
          </Group>
          {form.coverage === GOV && (
            <TextField id="assisted-funding-details" label="Which program or agency provides the funding?" value={form.funding_details} onChange={(v) => set("funding_details", v)} required maxLength={400} testId="assisted-input-funding-details" />
          )}
          {form.coverage === "Other" && (
            <TextField id="assisted-coverage-other" label="Please describe" value={form.coverage_other} onChange={(v) => set("coverage_other", v)} required maxLength={400} testId="assisted-input-coverage-other" />
          )}
          <TextArea id="assisted-notes" label="Anything else you would like us to know? (optional)" value={form.additional_notes} onChange={(v) => set("additional_notes", v)} max={1000} testId="assisted-input-notes" />
          <Notice>We cannot guarantee insurance reimbursement or eligibility for government funding.</Notice>
        </>
      ),
    },
    {
      title: "Your contact details", short: "Contact", validate: () => validateContact(form),
      content: (
        <>
          <ContactFields form={form} set={set} prefix="assisted" />
          <ConsentBlock form={form} set={set} prefix="assisted" purpose="interest list request" />
          <Honeypot value={form.website} onChange={(v) => set("website", v)} />
        </>
      ),
    },
  ];

  return (
    <Wizard steps={steps} testPrefix="assisted" submitting={submitting} error={error} submitLabel="Join the interest list"
      onSubmit={() => submit("/public/assisted-living-interest", buildPayload(clean(form)))} />
  );
};
